import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/shared/ui/dropdown-menu";
import { X, ChevronDown } from "lucide-react";

interface TargetsSelectorProps {
  targets: string[];
  availableTargets: string[];
  isLoading?: boolean;
  onTargetsChange: (targets: string[]) => void;
}

export function TargetsSelector({
  targets,
  availableTargets,
  isLoading,
  onTargetsChange,
}: TargetsSelectorProps) {
  const toggleTarget = (target: string) => {
    onTargetsChange(
      targets.includes(target)
        ? targets.filter((t) => t !== target)
        : [...targets, target]
    );
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-ink-800">Targets</label>
      {targets.length > 0 && (
        <div className="flex flex-wrap gap-2 rounded-lg border border-grid-200 bg-grid-50 p-3 min-h-[60px]">
          {targets.map((target) => (
            <Badge
              key={target}
              variant="secondary"
              className="flex items-center gap-1.5 pr-1"
            >
              {target}
              <button
                type="button"
                onClick={() => toggleTarget(target)}
                className="ml-1 rounded-full hover:bg-grid-200 p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between"
            disabled={isLoading || availableTargets.length === 0}
          >
            <span>
              {targets.length > 0
                ? `Add more targets (${targets.length} selected)`
                : availableTargets.length === 0
                  ? "No targets available"
                  : "Select targets"}
            </span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-h-60 w-(--radix-dropdown-menu-trigger-width) overflow-y-auto">
          {isLoading ? (
            <div className="px-2 py-6 text-center text-sm text-ink-500">
              Loading targets...
            </div>
          ) : availableTargets.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-ink-500">
              No targets available
            </div>
          ) : (
            availableTargets.map((target: string) => (
              <DropdownMenuCheckboxItem
                key={target}
                checked={targets.includes(target)}
                onCheckedChange={() => toggleTarget(target)}
              >
                {target}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

